import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CalendarClock, Clock, Upload, Download, History, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

export default function OperatingHours() {
  const {
    t
  } = useI18n();

  const [stores, setStores] = useState<any[]>([]);
  const [storeId, setStoreId] = useState<string>("");
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState("current");

  useEffect(() => {
    api.get("/org/stores").then((r) => {
      const list = r.data.stores || [];
      setStores(list);
      if (list[0]) setStoreId(list[0].id);
    }).catch(() => setStores([])).finally(() => setLoaded(true));
  }, []);

  if (!loaded) return <LoadingBlock />;
  if (stores.length === 0) {
    return (
      <div>
        <PageHeader title="Operating Hours" testId="ophours-header" />
        <EmptyState icon={<CalendarClock size={28} />} title="No stores in scope"
          description="You have no stores available to configure operating hours for." />
      </div>
    );
  }

  return (
    (<div>
      <PageHeader title="Operating Hours"
        subtitle="Versioned store & mall hours, staffing buffers, holidays and closures that govern scheduling"
        testId="ophours-header" />
      <div className="mb-4 flex items-center gap-2">
        <Label className="text-xs text-muted-foreground">{t("Store")}</Label>
        <select className="rounded-md border bg-background px-3 py-1.5 text-sm" value={storeId}
          onChange={(e) => setStoreId(e.target.value)} data-testid="ophours-store-select">
          {stores.map((s) => <option key={s.id} value={s.id}>#{s.store_number} · {s.name}</option>)}
        </select>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="current" data-testid="ophours-tab-current">{t("Current & Preview")}</TabsTrigger>
          <TabsTrigger value="editor" data-testid="ophours-tab-editor">{t("Editor")}</TabsTrigger>
          <TabsTrigger value="upload" data-testid="ophours-tab-upload">{t("Bulk Upload")}</TabsTrigger>
          <TabsTrigger value="history" data-testid="ophours-tab-history">{t("History")}</TabsTrigger>
        </TabsList>
        {storeId && <>
          <TabsContent value="current"><CurrentAndPreview storeId={storeId} /></TabsContent>
          <TabsContent value="editor"><Editor storeId={storeId} onDone={() => setTab("current")} /></TabsContent>
          <TabsContent value="upload"><BulkUpload onDone={() => setTab("history")} /></TabsContent>
          <TabsContent value="history"><VersionHistory storeId={storeId} /></TabsContent>
        </>}
      </Tabs>
    </div>)
  );
}

const CurrentAndPreview: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [data, setData] = useState<any>(null);
  const [days, setDays] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/calendar?store_id=${storeId}`),
      api.get(`/calendar/effective?store_id=${storeId}&start=${today()}&end=${plusDays(20)}`),
    ]).then(([c, e]) => { setData(c.data); setDays(e.data.days || []); }).catch(() => { setData(null); setDays([]); }).finally(() => setLoading(false));
  }, [storeId]);
  if (loading) return <LoadingBlock />;
  const pub = data?.published;
  return (
    (<div className="space-y-4" data-testid="ophours-current">
      {!pub ? (
        <EmptyState icon={<CalendarClock size={26} />} title="No published operating calendar"
          description="Use the Editor to set weekly hours and publish the first version." />
      ) : (
        <Card className="p-4" data-testid="ophours-published">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 font-display text-sm font-semibold"><Clock size={16} className="text-brand" />{t("Published calendar")}</div>
            <StatusPill status="good">v{pub.version_no}{" "}{t("· published")}</StatusPill>
          </div>
          <div className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
            {pub.weekly.map((w: any) => (
              <div key={w.weekday} className="flex justify-between border-b border-border/40 py-1">
                <span className="font-medium">{DAYS[w.weekday]}</span>
                <span className="text-muted-foreground">
                  {w.closed ? tl("Closed") : `${tl("Store")} ${w.store_open}–${w.store_close} · ${tl("Mall")} ${w.mall_open}–${w.mall_close}`}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-muted-foreground">{t("Staffing buffers:")}{" "}{pub.buffers.pre_open_minutes}{t("m before open ·")}{" "}{pub.buffers.post_close_minutes}{t("m after close · Effective from")}{" "}{pub.effective_start}
          </div>
        </Card>
      )}
      <Card className="p-4" data-testid="ophours-preview">
        <div className="mb-3 flex items-center gap-2 font-display text-sm font-semibold"><CalendarClock size={16} className="text-brand" />{t("Next 3 weeks (governing hours)")}</div>
        <div className="space-y-1">
          {days.map((d) => (
            <div key={d.date} className="flex items-center justify-between border-b border-border/30 py-1 text-sm" data-testid={`ophours-day-${d.date}`}>
              <span className="font-medium">{d.date} <span className="text-xs text-muted-foreground">{d.weekday_label}</span></span>
              {d.closed
                ? <span className="flex items-center gap-1 text-xs text-status-critical"><AlertTriangle size={12} />{t("Closed")}{d.label ? ` · ${d.label}` : ""}</span>
                : <span className="text-xs text-muted-foreground">{d.store_open}–{d.store_close}{d.label ? ` · ${d.label}` : ""}{d.source_kind === "exception" ? t("(exception)") : ""}</span>}
            </div>
          ))}
        </div>
      </Card>
    </div>)
  );
};

const blankWeekly = () => DAYS.map((_, i) => ({ weekday: i, mall_open: "09:00", mall_close: "21:00", store_open: "09:00", store_close: "20:00", closed: false }));

const Editor: React.FC<{ storeId: string; onDone: () => void }> = ({ storeId, onDone }) => {
  const {
    t
  } = useI18n();

  const [weekly, setWeekly] = useState<any[]>(blankWeekly());
  const [buffers, setBuffers] = useState({ pre_open_minutes: 30, post_close_minutes: 30 });
  const [exceptions, setExceptions] = useState<any[]>([]);
  const [effectiveStart, setEffectiveStart] = useState(today());
  const [reason, setReason] = useState("");
  const [draftId, setDraftId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/calendar?store_id=${storeId}`).then((r) => {
      const src = r.data.draft || r.data.published;
      if (src) {
        setWeekly(src.weekly?.length ? src.weekly : blankWeekly());
        setBuffers(src.buffers || { pre_open_minutes: 30, post_close_minutes: 30 });
        setExceptions(src.exceptions || []);
        setEffectiveStart(src.effective_start || today());
        setDraftId(r.data.draft ? r.data.draft.id : null);
      } else {
        setWeekly(blankWeekly()); setExceptions([]); setDraftId(null);
      }
    }).finally(() => setLoading(false));
  }, [storeId]);
  useEffect(() => { load(); }, [load]);

  const setDay = (i: number, k: string, v: any) => setWeekly((p) => p.map((w, j) => j === i ? { ...w, [k]: v } : w));
  const addException = () => setExceptions((p) => [...p, { date: plusDays(7), closed: false, emergency: false, mall_open: "09:00", mall_close: "21:00", store_open: "09:00", store_close: "20:00", label: "", source: "manual" }]);
  const setExc = (i: number, k: string, v: any) => setExceptions((p) => p.map((e, j) => j === i ? { ...e, [k]: v } : e));

  const saveDraft = async () => {
    if (!reason.trim()) { toast.error("A change reason is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/calendar", { store_id: storeId, weekly, buffers, exceptions, effective_start: effectiveStart, source: "manual", change_reason: reason });
      setDraftId(r.data.calendar.id);
      toast.success("Draft saved");
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };
  const publish = async () => {
    if (!draftId) { toast.error("Save a draft first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/calendar/${draftId}/publish`, {});
      const flagged = r.data.flagged_schedules || 0;
      toast.success(`${tl("Published")}${flagged ? tl(" · {n} schedule(s) flagged for revision", { n: flagged }) : ""}`);
      onDone();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed to publish"); }
    finally { setBusy(false); }
  };

  if (loading) return <LoadingBlock />;
  return (
    (<div className="space-y-4" data-testid="ophours-editor">
      <Card className="p-4">
        <div className="mb-3 font-display text-sm font-semibold">{t("Standard weekly hours")}</div>
        <div className="space-y-2">
          {weekly.map((w, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`ophours-week-${i}`}>
              <span className="w-10 font-medium">{DAYS[i]}</span>
              <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={w.closed} onChange={(e) => setDay(i, "closed", e.target.checked)} data-testid={`ophours-week-closed-${i}`} />{t("Closed")}</label>
              {!w.closed && <>
                <span className="text-xs text-muted-foreground">{t("Store")}</span>
                <Input type="time" className="w-28" value={w.store_open} onChange={(e) => setDay(i, "store_open", e.target.value)} />
                <span>–</span>
                <Input type="time" className="w-28" value={w.store_close} onChange={(e) => setDay(i, "store_close", e.target.value)} />
                <span className="text-xs text-muted-foreground">{t("Mall")}</span>
                <Input type="time" className="w-28" value={w.mall_open} onChange={(e) => setDay(i, "mall_open", e.target.value)} />
                <span>–</span>
                <Input type="time" className="w-28" value={w.mall_close} onChange={(e) => setDay(i, "mall_close", e.target.value)} />
              </>}
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">{t("Pre-open buffer (min)")}</Label><Input type="number" className="mt-1 w-32" value={buffers.pre_open_minutes} onChange={(e) => setBuffers((b) => ({ ...b, pre_open_minutes: Number(e.target.value) }))} data-testid="ophours-buffer-pre" /></div>
          <div><Label className="text-xs">{t("Post-close buffer (min)")}</Label><Input type="number" className="mt-1 w-32" value={buffers.post_close_minutes} onChange={(e) => setBuffers((b) => ({ ...b, post_close_minutes: Number(e.target.value) }))} data-testid="ophours-buffer-post" /></div>
          <div><Label className="text-xs">{t("Effective from")}</Label><Input type="date" className="mt-1 w-40" value={effectiveStart} onChange={(e) => setEffectiveStart(e.target.value)} data-testid="ophours-effective" /></div>
        </div>
      </Card>
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="font-display text-sm font-semibold">{t("Date exceptions, holidays & closures")}</div>
          <Button size="sm" variant="outline" onClick={addException} data-testid="ophours-add-exception"><Plus size={13} className="mr-1" />{t("Add")}</Button>
        </div>
        {exceptions.length === 0 ? <div className="text-xs text-muted-foreground">{t("No exceptions. Add holidays, closures or special hours.")}</div> : (
          <div className="space-y-2">
            {exceptions.map((e, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`ophours-exc-${i}`}>
                <Input type="date" className="w-40" value={e.date} onChange={(ev) => setExc(i, "date", ev.target.value)} />
                <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={e.closed} onChange={(ev) => setExc(i, "closed", ev.target.checked)} data-testid={`ophours-exc-closed-${i}`} />{t("Closed")}</label>
                {!e.closed && <>
                  <Input type="time" className="w-28" value={e.store_open || ""} onChange={(ev) => setExc(i, "store_open", ev.target.value)} />
                  <span>–</span>
                  <Input type="time" className="w-28" value={e.store_close || ""} onChange={(ev) => setExc(i, "store_close", ev.target.value)} />
                </>}
                <Input className="w-40" placeholder={t("Label")} value={e.label || ""} onChange={(ev) => setExc(i, "label", ev.target.value)} />
                <Button size="icon" variant="ghost" onClick={() => setExceptions((p) => p.filter((_, j) => j !== i))} data-testid={`ophours-exc-del-${i}`}><Trash2 size={14} /></Button>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card className="p-4">
        <Label className="text-xs">{t("Change reason (required)")}</Label>
        <Input className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("e.g. Q4 holiday hours")} data-testid="ophours-reason" />
        <div className="mt-3 flex gap-2">
          <Button variant="outline" onClick={saveDraft} disabled={busy} data-testid="ophours-save-draft">{t("Save draft")}</Button>
          <Button onClick={publish} disabled={busy || !draftId} data-testid="ophours-publish"><CheckCircle2 size={14} className="mr-1" />{t("Publish")}</Button>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">{t(
          "Published versions are immutable. Publishing creates a new version and never rewrites the hours governing an already-published schedule."
        )}</div>
      </Card>
    </div>)
  );
};

const BulkUpload: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const {
    t
  } = useI18n();

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [csvText, setCsvText] = useState<string>("");
  const [effectiveStart, setEffectiveStart] = useState(today());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    const r = await api.get("/calendar/upload/template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "operating_hours_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const doPreview = async (f: File) => {
    const text = await f.text();
    setCsvText(text);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/calendar/upload/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };
  const commit = async () => {
    if (!reason.trim()) { toast.error("A change reason is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/calendar/upload/commit", { import_id: `ui-${Date.now()}`, csv: csvText, effective_start: effectiveStart, change_reason: reason });
      toast.success(tl("Committed {rows} row(s) into {n} draft(s) — review & publish in Editor/History", { rows: r.data.rows, n: r.data.created_drafts.length }));
      onDone();
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Commit failed"));
    } finally { setBusy(false); }
  };

  return (
    (<div className="space-y-4" data-testid="ophours-upload">
      <Card className="p-4">
        <div className="mb-3 flex items-center gap-2 font-display text-sm font-semibold"><Upload size={16} className="text-brand" />{t("Bulk upload date-specific hours")}</div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="ophours-download-template"><Download size={13} className="mr-1" />{t("CSV template")}</Button>
          <input type="file" accept=".csv" data-testid="ophours-file"
            onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); if (f) doPreview(f); }} />
        </div>
        <div className="mt-2 text-xs text-muted-foreground">{t(
          "Columns: store_code, date, mall_open, mall_close, store_open, store_close, closed, label, source. Store codes outside your scope are rejected."
        )}</div>
      </Card>
      {preview && (
        <Card className="p-4" data-testid="ophours-preview-table">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-semibold">{t("Preview —")}{" "}{preview.valid_rows}/{preview.total_rows}{" "}{t("valid rows")}</div>
            {preview.can_commit ? <StatusPill status="good">{t("ready to commit")}</StatusPill> : <StatusPill status="critical">{t("fix errors")}</StatusPill>}
          </div>
          <div className="max-h-64 space-y-1 overflow-auto">
            {preview.rows.map((row: any) => (
              <div key={row.line} className={`flex items-center justify-between rounded px-2 py-1 text-xs ${row.valid ? "" : "bg-status-critical/10"}`} data-testid={`ophours-preview-row-${row.line}`}>
                <span>#{row.store_code} · {row.date} · {row.closed ? t("closed") : `${row.store_open || "—"}–${row.store_close || "—"}`} {row.label ? `· ${row.label}` : ""}</span>
                {row.valid ? <CheckCircle2 size={13} className="text-status-good" /> : <span className="text-status-critical">{row.errors[0]}</span>}
              </div>
            ))}
          </div>
          {preview.errors?.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-status-critical" data-testid="ophours-preview-errors">
              {preview.errors.slice(0, 6).map((er: string, i: number) => <li key={i}>{er}</li>)}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">{t("Effective from")}</Label><Input type="date" className="mt-1 w-40" value={effectiveStart} onChange={(e) => setEffectiveStart(e.target.value)} /></div>
            <div className="flex-1"><Label className="text-xs">{t("Change reason")}</Label><Input className="mt-1" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ophours-commit-reason" /></div>
            <Button onClick={commit} disabled={busy || !preview.can_commit} data-testid="ophours-commit">{t("Commit to drafts")}</Button>
          </div>
        </Card>
      )}
    </div>)
  );
};

const VersionHistory: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/calendar?store_id=${storeId}`).then((r) => setRows(r.data.versions || [])).finally(() => setLoading(false));
  }, [storeId]);
  useEffect(() => { load(); }, [load]);
  const dup = async (id: string) => {
    try { await api.post(`/calendar/${id}/duplicate`, {}); toast.success("Copied to a new editable draft (rollback)"); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const publish = async (id: string) => {
    try { await api.post(`/calendar/${id}/publish`, {}); toast.success("Published"); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (loading) return <LoadingBlock />;
  if (rows.length === 0) return <EmptyState icon={<History size={26} />} title="No versions yet" description="Saved and published calendar versions appear here." />;
  const statusColor: Record<string, string> = { published: "good", draft: "warn", superseded: "info", archived: "critical" };
  return (
    (<div className="space-y-2" data-testid="ophours-history">
      {rows.map((v) => (
        <Card key={v.id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid={`ophours-version-${v.version_no}`}>
          <div>
            <div className="text-sm font-medium">{t("Version")}{" "}{v.version_no}</div>
            <div className="text-xs text-muted-foreground">{t("Effective")}{" "}{v.effective_start}· {v.change_reason}</div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill status={statusColor[v.status] || "info"}>{stat(v.status)}</StatusPill>
            {v.status === "draft" && <Button size="sm" onClick={() => publish(v.id)} data-testid={`ophours-version-publish-${v.version_no}`}>{t("Publish")}</Button>}
            {(v.status === "published" || v.status === "superseded" || v.status === "archived") &&
              <Button size="sm" variant="outline" onClick={() => dup(v.id)} data-testid={`ophours-version-dup-${v.version_no}`}>{t("Duplicate")}</Button>}
          </div>
        </Card>
      ))}
    </div>)
  );
};
